import { useState } from 'react';
import {
  useListAdvancesRetentionsQuery,
  useCreateAdvanceRetentionMutation,
  useListTaxCalendarQuery,
  useCreateTaxItemMutation,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconX } from '@shared/ui/icons';
import { useAgentPermission } from '@entities/user';
import {
  Container,
  AlertBanner,
} from './styled';
import { TaxBalanceSection } from './TaxBalanceSection';
import { AdvancesSection } from './AdvancesSection';
import { RetentionsSection } from './RetentionsSection';
import { CreateTaxModal } from './CreateTaxModal';
import { CreateAdvanceRetentionModal } from './CreateAdvanceRetentionModal';

export function WorkingCapitalView() {
  const { can_write: canWrite } = useAgentPermission('finance');
  const {
    data: advances,
    isLoading: isAdvLoading,
    isError: isAdvError,
    refetch: refetchAdv,
  } = useListAdvancesRetentionsQuery();
  const {
    data: taxData,
    isLoading: isTaxLoading,
    isError: isTaxError,
    refetch: refetchTax,
  } = useListTaxCalendarQuery();

  const [createTaxItem, { isLoading: isCreatingTax }] = useCreateTaxItemMutation();
  const [createAdvRetention, { isLoading: isCreatingAdv }] = useCreateAdvanceRetentionMutation();

  const [taxModalOpen, setTaxModalOpen] = useState(false);
  const [advModalOpen, setAdvModalOpen] = useState(false);
  const [banner, setBanner] = useState<{ text: string; tone: 'success' | 'warning' } | null>(null);

  // Разделение авансов и гарантийных удержаний
  const advanceRows = (advances ?? []).filter(
    (a) => a.direction === 'payable_advance' || a.direction === 'receivable_advance',
  );
  const retentionRows = (advances ?? []).filter(
    (a) => a.direction === 'receivable_retention' || a.direction === 'payable_retention',
  );

  async function handleCreateTax(data: {
    taxPeriod: string;
    taxDueDate: string;
    taxType: string;
    taxTitle: string;
    taxAmount: number;
    taxReserved?: number;
  }) {
    try {
      await createTaxItem({
        tax_period: data.taxPeriod,
        due_date: data.taxDueDate,
        tax_type: data.taxType,
        title: data.taxTitle,
        accrued_amount: data.taxAmount,
        reserved_amount: data.taxReserved,
      }).unwrap();
      setTaxModalOpen(false);
      setBanner({
        text: 'Налоговое обязательство успешно добавлено в календарь ЕНС.',
        tone: 'success',
      });
      refetchTax();
    } catch {
      setBanner({
        text: 'Не удалось сохранить налоговое обязательство. Проверьте введённые данные.',
        tone: 'warning',
      });
    }
  }

  async function handleCreateAdv(data: {
    direction: string;
    counterpartyName: string;
    contractNumber: string;
    projectName: string;
    initialAmount: number;
    expectedCloseDate?: string;
  }) {
    try {
      await createAdvRetention({
        direction: data.direction,
        counterparty_name: data.counterpartyName,
        contract_number: data.contractNumber,
        project_name: data.projectName,
        initial_amount: data.initialAmount,
        expected_close_date: data.expectedCloseDate,
      }).unwrap();
      setAdvModalOpen(false);
      setBanner({
        text: 'Запись об авансе / гарантийном удержании успешно добавлена.',
        tone: 'success',
      });
      refetchAdv();
    } catch {
      setBanner({
        text: 'Не удалось сохранить запись об авансе / удержании. Проверьте введённые данные.',
        tone: 'warning',
      });
    }
  }

  return (
    <Container>
      {banner && (
        <AlertBanner $tone={banner.tone}>
          <span>{banner.text}</span>
          <Button $variant="ghost" $size="sm" onClick={() => setBanner(null)} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </AlertBanner>
      )}

      {/* Секция 1: Налоговый баланс НДС и ЕНС */}
      <TaxBalanceSection
        taxData={taxData}
        isTaxLoading={isTaxLoading}
        isTaxError={isTaxError}
        refetchTax={refetchTax}
        onOpenTaxModal={() => setTaxModalOpen(true)}
        canWrite={Boolean(canWrite)}
      />

      {/* Секция 2: Неотработанные авансы */}
      <AdvancesSection
        advanceRows={advanceRows}
        isLoading={isAdvLoading}
        isError={isAdvError}
        refetch={refetchAdv}
        onOpenAdvModal={() => setAdvModalOpen(true)}
        canWrite={Boolean(canWrite)}
      />

      {/* Секция 3: Гарантийные удержания */}
      <RetentionsSection retentionRows={retentionRows} />

      {/* Модалка: Добавление налога ЕНС */}
      <CreateTaxModal
        open={taxModalOpen}
        onClose={() => setTaxModalOpen(false)}
        onCreateTax={handleCreateTax}
        isCreating={isCreatingTax}
      />

      {/* Модалка: Добавление аванса / удержания */}
      <CreateAdvanceRetentionModal
        open={advModalOpen}
        onClose={() => setAdvModalOpen(false)}
        onCreateAdv={handleCreateAdv}
        isCreating={isCreatingAdv}
      />
    </Container>
  );
}
